import { useQuery } from "@apollo/client";
import { GET_BANDS } from "./graphql";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { DataGrid, GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { LinkButton } from "../../components/LinkButton";
import { PageContainer } from "../../components/PageContainer";
import { LoadingOverlay } from "../../components/Loading";
import HasImageIcon from "../../components/HasImageIcon";
import { QueryMode, SortOrder } from "../../__generated__/graphql";
import { formatFileSize, getImageFileSize } from "../band/edit/getImageSize";
import { useIsSuperAdmin } from "../../App";
import { getUpdatedImageUrl } from "../post/edit/helpers";
const columns: GridColDef[] = [
  { field: "id", headerName: "ID", width: 70 },
  { field: "name", headerName: "Name", width: 130 },
  {
    field: "bookings",
    headerName: "Bookings",
    description: "This column has a value getter and is not sortable.",
    sortable: false,
    width: 160,
    valueGetter: (params: GridValueGetterParams) =>
      `${params.row.bookings.length}`,
  },
];

const NO_BOOKING_FILTER = { bookings: { none: { id: { not: null } } } };

const NUMBER_PER_PAGE = 20;
type SortOption = "current" | "name_asc" | "name_desc";

const Bands = () => {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [sortOption, setSortOption] = React.useState<SortOption>("name_asc");
  const [onlyBandsWithoutBookings, setOnlyBandsWithoutBookings] =
    React.useState(false);
  const [getImageSize, setGetImageSize] = useState(false);

  const bandsFilter = !!onlyBandsWithoutBookings
    ? NO_BOOKING_FILTER
    : undefined;
  const orderBy =
    sortOption === "name_asc"
      ? [{ name: SortOrder.Asc }]
      : sortOption === "name_desc"
        ? [{ name: SortOrder.Desc }]
        : undefined;

  const { data, error, loading } = useQuery(GET_BANDS, {
    fetchPolicy: "cache-first",
    variables: {
      skip: (page - 1) * NUMBER_PER_PAGE,
      take: NUMBER_PER_PAGE,
      ...(orderBy ? { orderBy } : {}),
      where:
        search.length > 0
          ? {
              name: {
                contains: search,
                mode: QueryMode.Insensitive,
              },
              ...bandsFilter,
            }
          : bandsFilter,
    },
  });
  console.log('orderBy', orderBy)
  const isSuperAdmin = useIsSuperAdmin();

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <PageContainer title="Bands">
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 1, flexWrap: { xs: "wrap", sm: "nowrap" } }}
      >
        <TextField
          id="outlined-basic"
          label="Search"
          variant="outlined"
          placeholder="Search..."
          sx={{ width: { xs: "100%", sm: "40%" } }}
          onChange={(value) => setSearch(value.target.value)}
        />
        <TextField
          id="bands-sort"
          label="Sorting"
          variant="outlined"
          select
          value={sortOption}
          onChange={(event) => {
            setSortOption(event.target.value as SortOption);
            setPage(1);
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="current">Default</MenuItem>
          <MenuItem value="name_asc">Name (A-Z)</MenuItem>
          <MenuItem value="name_desc">Name (Z-A)</MenuItem>
        </TextField>
        <FormControlLabel
          sx={{ m: 0, height: 56, alignItems: "center" }}
          control={
            <Checkbox
              value={onlyBandsWithoutBookings}
              onClick={() =>
                setOnlyBandsWithoutBookings(!onlyBandsWithoutBookings)
              }
            />
          }
          label={"Exclude bands with bookings"}
        />
        {isSuperAdmin && (
          <FormControlLabel
            sx={{ m: 0, height: 56, alignItems: "center" }}
            control={
              <Checkbox
                value={getImageSize}
                onClick={() => setGetImageSize(!getImageSize)}
              />
            }
            label={"Show image sizes"}
          />
        )}
        <Box sx={{ height: 56, display: "flex", alignItems: "center" }}>
          <LinkButton
            path="/band/"
            text="Create new band"
            variant={"contained"}
          />
        </Box>
      </Stack>
      <Pagination
        count={Math.round(data?.bandCount! / NUMBER_PER_PAGE)}
        onChange={handleChange}
        page={page}
      />
      <LoadingOverlay loading={loading}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left">name</TableCell>
                <TableCell align="left">bookings</TableCell>
                <TableCell align="left">has image</TableCell>
                {getImageSize && <TableCell align="left">has image</TableCell>}
                <TableCell align="left">actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.bands?.map((row) => (
                <TableRow
                  key={row.name}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align="left">{row.name}</TableCell>
                  <TableCell align="left">{row?.bookings?.length}</TableCell>
                  <TableCell align="left">
                    <HasImageIcon
                      hasImage={
                        !!(row.imageUrl && row.imageUrl.trim().length > 0)
                      }
                    />
                  </TableCell>
                  {getImageSize && (
                    <TableCell align="left">
                      <ImageSize imageUrl={row.imageUrl} />
                    </TableCell>
                  )}
                  <TableCell align="left">
                    <LinkButton path={`/band/${row.id}`} text="View" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </LoadingOverlay>
    </PageContainer>
  );
};

export const ImageSize = ({
  imageUrl,
}: {
  imageUrl: string | undefined | null;
}) => {
  const [imageSize, setImageSize] = useState<string | undefined>("");

  useEffect(() => {
    (async () => {
      const url = getUpdatedImageUrl(imageUrl ?? "");
      const imageSize = await getImageFileSize(url ?? undefined).then((res) => {
        if (!imageUrl || imageUrl === "" || typeof res === "string") {
          return "";
        }

        return formatFileSize(res as number);
      });
      setImageSize(imageSize ?? "");
    })();
  }, [imageUrl]);

  return (
    <div
      style={{
        ...(imageSize?.toLowerCase().includes("mb") && {
          color: "red",
          fontWeight: "bold",
        }),
      }}
    >
      {imageSize}
    </div>
  );
};

export default Bands;
